import { useTheme } from "@emotion/react";
import { Card, CardContainer } from "../../../../components/Card";
import { StatCard } from "../overview/OverviewHelper";
import { AreaChart, BarChart, LineChart } from "../../../../components/Charts";

const TargetView = ({ filterBy, filteredData = [] }) => {
    const theme = useTheme() || {};

    const getCumulativeData = () => {
        const cumulative = [];
        let runningTotals = {};

        filteredData.forEach((week = {}) => {
            const cumulativeWeek = { ...week };
            const cumulativeFields = [
                "scaled_billed",
                "scaled_proposed",
                "scaled_closed",
                "collabs_target",
                "collabs_billed",
                "affiliate_target",
                "affiliate_billed",
                "scaled_target",
            ];

            cumulativeFields.forEach((field) => {
                const currentValue = Number(week[field]) || 0;
                runningTotals[field] =
                    (runningTotals[field] || 0) + currentValue;
                cumulativeWeek[`${field}_Cumulative`] = runningTotals[field];
            });

            cumulative.push(cumulativeWeek);
        });

        return cumulative;
    };

    const dataToUse =
        filterBy === "cumulativeView" ? getCumulativeData() : filteredData;

    const safeReduce = (arr, key) =>
        arr.reduce((sum, item = {}) => sum + (Number(item[key]) || 0), 0);

    const totalCollabsTarget = safeReduce(dataToUse, "collabs_target");
    const totalCollabsBilled = safeReduce(dataToUse, "collabs_billed");
    const totalAffiliateTarget = safeReduce(dataToUse, "affiliate_target");
    const totalAffiliateBilled = safeReduce(dataToUse, "affiliate_billed");
    const totalScaledTarget = safeReduce(dataToUse, "scaled_target");
    const totalScaledBilled = safeReduce(dataToUse, "scaled_billed");

    const collabsAchievement =
        totalCollabsTarget && totalCollabsBilled
            ? (totalCollabsBilled / totalCollabsTarget) * 100
            : 0;

    const affiliateAchievement =
        totalAffiliateTarget && totalAffiliateBilled
            ? (totalAffiliateBilled / totalAffiliateTarget) * 100
            : 0;

    const scaledAchievement =
        totalScaledTarget && totalScaledBilled
            ? (totalScaledBilled / totalScaledTarget) * 100
            : 0;

    const overallAchievement =
        (collabsAchievement + affiliateAchievement + scaledAchievement) / 3;

    const stats = [
        {
            label: "Collabs Achievement",
            value: `${collabsAchievement.toFixed(1)}%`,
            color: theme?.palette?.graph?.cyan || "#00BCD4",
        },
        {
            label: "Affiliate Achievement",
            value: `${affiliateAchievement.toFixed(1)}%`,
            color: theme?.palette?.graph?.yellow || "#FFEB3B",
        },
        {
            label: "Scaled Achievement",
            value: `${scaledAchievement.toFixed(1)}%`,
            color: theme?.palette?.graph?.magenta || "#E91E63",
        },
        {
            label: "Overall Achievement",
            value: `${overallAchievement.toFixed(1)}%`,
            color: theme?.palette?.graph?.green || "#4CAF50",
        },
    ];

    const achievementData = dataToUse.map((week = {}) => ({
        ...week,
        CollabsAchievement:
            week.collabs_target && week.collabs_billed
                ? (
                      (Number(week.collabs_billed) /
                          Number(week.collabs_target)) *
                      100
                  ).toFixed(2)
                : "0",
        CollabsAchievement_Cumulative:
            week.collabs_target_Cumulative && week.collabs_billed_Cumulative
                ? (
                      (Number(week.collabs_billed_Cumulative) /
                          Number(week.collabs_target_Cumulative)) *
                      100
                  ).toFixed(2)
                : "0",
        AffiliateAchievement:
            week.affiliate_target && week.affiliate_billed
                ? (
                      (Number(week.affiliate_billed) /
                          Number(week.affiliate_target)) *
                      100
                  ).toFixed(2)
                : "0",
        AffiliateAchievement_Cumulative:
            week.affiliate_target_Cumulative && week.affiliate_billed_Cumulative
                ? (
                      (Number(week.affiliate_billed_Cumulative) /
                          Number(week.affiliate_target_Cumulative)) *
                      100
                  ).toFixed(2)
                : "0",
        ScaledAchievement:
            week.scaled_target && week.scaled_billed
                ? (
                      (Number(week.scaled_billed) /
                          Number(week.scaled_target)) *
                      100
                  ).toFixed(2)
                : "0",
        ScaledAchievement_Cumulative:
            week.scaled_target_Cumulative && week.scaled_billed_Cumulative
                ? (
                      (Number(week.scaled_billed_Cumulative) /
                          Number(week.scaled_target_Cumulative)) *
                      100
                  ).toFixed(2)
                : "0",
    }));

    return (
        <div>
            <CardContainer>
                {stats.map((stat, index) => (
                    <Card key={index}>
                        <StatCard
                            label={stat.label}
                            value={stat.value}
                            color={stat.color}
                        />
                    </Card>
                ))}
            </CardContainer>
            <CardContainer>
                <BarChart
                    data={dataToUse}
                    series={
                        filterBy === "weeklyView"
                            ? [
                                  {
                                      name: "Collabs Target",
                                      key: "collabs_target",
                                  },
                                  {
                                      name: "Collabs Actual",
                                      key: "collabs_billed",
                                  },
                                  {
                                      name: "Affiliate Target",
                                      key: "affiliate_target",
                                  },
                                  {
                                      name: "Affiliate Actual",
                                      key: "affiliate_billed",
                                  },
                              ]
                            : [
                                  {
                                      name: "Collabs Target (Cumulative)",
                                      key: "collabs_target_Cumulative",
                                  },
                                  {
                                      name: "Collabs Actual (Cumulative)",
                                      key: "collabs_billed_Cumulative",
                                  },
                                  {
                                      name: "Affiliate Target (Cumulative)",
                                      key: "affiliate_target_Cumulative",
                                  },
                                  {
                                      name: "Affiliate Actual (Cumulative)",
                                      key: "affiliate_billed_Cumulative",
                                  },
                              ]
                    }
                    xKey="week"
                    title="Target vs Actual Revenue"
                />
                <LineChart
                    data={achievementData}
                    series={
                        filterBy === "weeklyView"
                            ? [
                                  {
                                      name: "Collabs %",
                                      key: "CollabsAchievement",
                                  },
                                  {
                                      name: "Affiliate %",
                                      key: "AffiliateAchievement",
                                  },
                                  {
                                      name: "Scaled %",
                                      key: "ScaledAchievement",
                                  },
                              ]
                            : [
                                  {
                                      name: "Collabs % (Cumulative)",
                                      key: "CollabsAchievement_Cumulative",
                                  },
                                  {
                                      name: "Affiliate % (Cumulative)",
                                      key: "AffiliateAchievement_Cumulative",
                                  },
                                  {
                                      name: "Scaled % (Cumulative)",
                                      key: "ScaledAchievement_Cumulative",
                                  },
                              ]
                    }
                    xKey="week"
                    title="Target Achievement Trend"
                />
            </CardContainer>
            <CardContainer>
                <AreaChart
                    data={dataToUse}
                    series={
                        filterBy === "weeklyView"
                            ? [
                                  {
                                      name: "Scaled Billed ",
                                      key: "scaled_billed",
                                  },
                                  {
                                      name: "Scaled Proposed",
                                      key: "scaled_proposed",
                                  },
                                  {
                                      name: "Scaled Closed",
                                      key: "scaled_closed",
                                  },
                              ]
                            : [
                                  {
                                      name: "Scaled Billed (Cumulative) ",
                                      key: "scaled_billed_Cumulative",
                                  },
                                  {
                                      name: "Scaled Proposed (Cumulative)",
                                      key: "scaled_proposed_Cumulative",
                                  },
                                  {
                                      name: "Scaled Closed (Cumulative)",
                                      key: "scaled_closed_Cumulative",
                                  },
                              ]
                    }
                    xKey="week"
                    title="Scaled Operations Funnel"
                />
            </CardContainer>
        </div>
    );
};

export default TargetView;
